import React from 'react';
import { useConfig } from '../hooks/useConfig';
import './ProgressTracker.css';

/**
 * ProgressTracker Component
 * Displays user progress, streaks, and level
 */
function ProgressTracker({
  totalPoints = 0,
  currentStreak = 0,
  lessonsCompleted = 0,
  totalLessons = 50
}) {
  const { config } = useConfig();
  const levels = config?.gamification?.levels || [
    { level: 1, name: 'Beginner', icon: '🌱', points: 0 },
    { level: 2, name: 'Learner', icon: '🌿', points: 500 },
    { level: 3, name: 'Coder', icon: '🌳', points: 1500 },
    { level: 4, name: 'Expert', icon: '🏆', points: 5000 },
    { level: 5, name: 'Master', icon: '👑', points: 10000 }
  ];

  const getCurrentLevel = () => {
    for (let i = levels.length - 1; i >= 0; i--) {
      if (totalPoints >= levels[i].points) {
        return levels[i];
      }
    }
    return levels[0];
  };

  const getNextLevel = () => {
    const current = getCurrentLevel();
    const nextIndex = levels.findIndex(l => l.level === current.level) + 1;
    return levels[nextIndex] || null;
  };

  const currentLevel = getCurrentLevel();
  const nextLevel = getNextLevel();
  
  const progressToNext = nextLevel 
    ? Math.min(100, ((totalPoints - currentLevel.points) / (nextLevel.points - currentLevel.points)) * 100)
    : 100;

  return (
    <div className="progress-tracker">
      {/* Stats Row */}
      <div className="progress-stats">
        {/* Streak Card */}
        <div className={`progress-stat-card streak ${currentStreak > 0 ? 'active' : ''}`}>
          <div className="progress-stat-icon">🔥</div>
          <div className="progress-stat-value">{currentStreak}</div>
          <div className="progress-stat-label">Day Streak</div>
        </div>

        {/* Points Card */}
        <div className="progress-stat-card points">
          <div className="progress-stat-icon">⭐</div>
          <div className="progress-stat-value">{totalPoints}</div>
          <div className="progress-stat-label">Total XP</div>
        </div>

        {/* Lessons Card */}
        <div className="progress-stat-card">
          <div className="progress-stat-icon">📚</div>
          <div className="progress-stat-value">{lessonsCompleted}</div>
          <div className="progress-stat-label">Lessons</div>
        </div>
      </div>

      {/* Level Progress */}
      <div className="level-progress-container">
        <div className="level-header">
          <div className="level-info">
            <div className="level-badge">{currentLevel.icon}</div>
            <div>
              <div className="level-name">{currentLevel.name}</div>
              <div className="level-label">Level {currentLevel.level}</div>
            </div>
          </div>
          
          {nextLevel && (
            <div className="next-level">
              <div className="next-level-label">Next Level</div>
              <div className="next-level-points">{nextLevel.points - totalPoints} XP</div>
            </div>
          )}
        </div>

        <div className="level-progress-bar">
          <div 
            className="level-progress-fill"
            style={{ width: `${progressToNext}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default ProgressTracker;